import { Link, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import type { PlayerListItem } from '@/hooks/usePlayers';
import {
  ageFromDob,
  birthdayTouchpoint,
  classificationLabel,
  priorityLabel,
} from '@/lib/formatters';
import { PlayerAvatar } from '@/components/PlayerAvatar';

export function PlayerRosterRow({ player }: { player: PlayerListItem }) {
  const location = useLocation();
  const name = player.displayName ?? `${player.firstName} ${player.lastName}`;
  const age = ageFromDob(player.dob ?? null);
  const birthday = birthdayTouchpoint(player.dob ?? null);
  const teamPart = player.currentTeam || '—';
  const leaguePart = player.currentLeague || '—';
  const positionPart = player.position || '—';
  const priority = priorityLabel(player.prospectPriority);
  const hasClientContact = Boolean(player.primaryEmail || player.cellPhone);
  const hasContractSource = Boolean(player.recentContractDetails);

  return (
    <Link
      to={`/people/${player.id}`}
      state={{ from: `${location.pathname}${location.search}` }}
      className="group flex items-center gap-3 border-b border-border bg-white px-3 py-3 transition-all last:border-b-0 hover:bg-bartlettIce hover:shadow-[inset_3px_0_0_#B8944D] sm:gap-4 sm:px-4"
    >
      <PlayerAvatar
        firstName={player.firstName}
        lastName={player.lastName}
        displayName={name}
        nhlId={player.nhlId}
        imageUrl={player.headshotUrl ?? player.heroImageUrl}
        mediaChecked={Boolean(player.mediaCheckedAt)}
        className="h-11 w-11 rounded-full transition-transform group-hover:scale-[1.03]"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="truncate text-[15px] font-semibold text-ink group-hover:text-bartlett">
            {name}
          </span>
          {priority && (
            <span className="font-mono text-[11px] font-semibold text-bartlett">{priority}</span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-[12px] text-muted">
          <span>{leaguePart}</span>
          <span>·</span>
          <span className="truncate">{teamPart}</span>
          <span>·</span>
          <span>{positionPart}</span>
          {player.shoots && (
            <>
              <span>·</span>
              <span>{player.shoots}</span>
            </>
          )}
          {age != null && (
            <>
              <span>·</span>
              <span>{age}y</span>
            </>
          )}
          {birthday && (
            <>
              <span>·</span>
              <span className="font-mono text-[11px] font-semibold uppercase tracking-wide text-bartlett">
                {birthday.label}
              </span>
            </>
          )}
          {hasClientContact && (
            <>
              <span>·</span>
              <span className="font-mono text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                contact
              </span>
            </>
          )}
          {hasContractSource && (
            <>
              <span>·</span>
              <span className="font-mono text-[11px] font-semibold uppercase tracking-wide text-bartlett">
                contract
              </span>
            </>
          )}
        </div>
      </div>
      <div className="hidden sm:block">
        <span className="rounded-full border border-border bg-white px-2 py-0.5 font-mono text-[11px] uppercase tracking-wide text-muted">
          {classificationLabel(player.classification)}
        </span>
      </div>
      {player.hasActiveInjury === 1 && (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-flag-warn/30 bg-flag-warn/5 px-2 py-0.5 font-mono text-[11px] text-flag-warn"
          aria-label="active injury"
        >
          <AlertCircle size={11} />
          <span className="hidden sm:inline">injured</span>
        </span>
      )}
    </Link>
  );
}
